<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">{{
        $t(`Publish ${selectedVersion.version_number}`)
      }}</span>
    </v-card-title>

    <v-card-text>
      <v-form>
        <v-select
          v-model="selectedTenants"
          :disabled="!tenants.length"
          class="mb-4"
          :label="$t('Select Tenants')"
          item-text="name"
          item-value="id"
          hide-details
          :items="tenants"
          outlined
          dense
          multiple
          chips
          deletable-chips
          @change="handleTenantsChange"
        >
          <template #prepend-item>
            <v-list-item ripple @click="toggleSelectAll">
              <v-list-item-action>
                <v-checkbox
                  :input-value="isAllSelected"
                  :indeterminate="isIndeterminate"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ $t('Select All') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
        </v-select>

        <v-select
          v-if="showListingSelect"
          v-model="selectedListing"
          :disabled="!isSingleTenantSelected"
          class="mb-4"
          :label="$t('Listing (optional)')"
          item-text="nickname"
          return-object
          hide-details
          :items="listingsNoScope"
          outlined
          dense
        >
          <template #prepend>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-icon small color="info" v-bind="attrs" v-on="on">
                  mdi-information
                </v-icon>
              </template>
              <span>{{
                $t(
                  'Listing selection is only available when a single tenant is selected'
                )
              }}</span>
            </v-tooltip>
          </template>
        </v-select>

        <v-select
          v-model="selectedAiProvider"
          class="mb-4"
          :label="$t('AI Provider')"
          hide-details
          :items="aiProviders"
          outlined
          dense
        ></v-select>

        <v-select
          v-if="selectedAiProvider"
          v-model="selectedAiModel"
          class="mb-4"
          :label="$t('AI Provider Model')"
          hide-details
          :items="aiModels[selectedAiProvider]"
          outlined
          dense
        ></v-select>

        <v-select
          v-if="showReasoningEffort"
          v-model="selectedReasoningEffort"
          :label="$t('Reasoning Effort')"
          hide-details
          :items="reasoningEffortOptions"
          outlined
          dense
        ></v-select>

        <v-card-actions class="justify-end">
          <v-btn color="primary" outlined @click="close">
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn color="primary" :disabled="!isSubmitEnabled" @click="submit">
            {{ $t('Submit') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  props: ['selectedVersion'],

  data() {
    return {
      selectedTenants: [],
      selectedListing: null,
      tenants: [],
      isAllSelected: false,
      isIndeterminate: false,
      aiProviders: [
        { value: 'openai', text: 'OpenAI' },
        { value: 'deepseek', text: 'DeepSeek' },
      ],
      aiModels: {
        openai: [
          { value: 'gpt-5.2', text: 'GPT-5.2' },
          { value: 'gpt-5.1', text: 'GPT-5.1' },
          { value: 'gpt-5', text: 'GPT-5' },
          { value: 'gpt-5-mini', text: 'GPT-5-mini' },
          { value: 'gpt-5-nano', text: 'GPT-5-nano' },
          { value: 'gpt-4.1', text: 'GPT-4.1' },
          { value: 'o3', text: 'o3' },
          { value: 'o4-mini', text: 'o4-mini' },
          { value: 'gpt-4o-2024-11-20', text: 'GPT-4o-2024-11-20' },
          { value: 'gpt-4o', text: 'GPT-4o' },
          { value: 'o3-mini', text: 'o3-mini' },
        ],
        deepseek: [
          { value: 'deepseek-chat', text: 'deepseek-chat' },
          { value: 'deepseek-reasoner', text: 'deepseek-reasoner' },
        ],
      },
      selectedAiProvider: 'openai',
      selectedAiModel: 'gpt-4o',
      modelsWithReasoningEffort: ['gpt-5.1', 'gpt-5.2'],
      reasoningEffortOptions: [
        { value: 'none', text: 'None' },
        { value: 'low', text: 'Low' },
        { value: 'medium', text: 'Medium' },
        { value: 'high', text: 'High' },
      ],
      selectedReasoningEffort: 'medium',
    }
  },

  computed: {
    listingsNoScope() {
      return this.$store.state.listingsNoScope
    },

    isSingleTenantSelected() {
      return this.selectedTenants.length === 1
    },

    showListingSelect() {
      return this.isSingleTenantSelected
    },

    showReasoningEffort() {
      return this.modelsWithReasoningEffort.includes(this.selectedAiModel)
    },

    isSubmitEnabled() {
      return this.selectedTenants.length > 0
    },
  },

  watch: {
    selectedTenants(val) {
      if (val.length !== 1) {
        this.selectedListing = null
      }
      this.isAllSelected = val.length === this.tenants.length
      this.isIndeterminate = val.length > 0 && val.length < this.tenants.length
    },
    selectedAiProvider(val) {
      this.selectedAiModel = this.aiModels[val][0].value
    },
    selectedAiModel(val) {
      if (!this.modelsWithReasoningEffort.includes(val)) {
        this.selectedReasoningEffort = 'medium'
      }
    },
  },

  mounted() {
    this.fetchTenants()
  },

  methods: {
    ...mapActions('tenants', ['getTenants']),

    async submit() {
      this.$store.commit('hideModal')
      const params = {
        ai_version_id: this.selectedVersion.id,
        tenant_id: this.selectedTenants,
        listing_id: this.isSingleTenantSelected
          ? this.selectedListing?.id
          : null,
        ai_provider: this.selectedAiProvider,
        ai_model: this.selectedAiModel,
        reasoning_effort: this.showReasoningEffort
          ? this.selectedReasoningEffort
          : null,
      }
      this.$store.dispatch('deployAiVersionControl', params)
    },

    close() {
      this.$store.commit('hideModal')
      this.selectedTenants = []
      this.selectedListing = null
      this.selectedReasoningEffort = 'medium'
    },

    async fetchTenants() {
      const { data } = await this.getTenants()
      this.tenants = data
    },

    async handleTenantsChange() {
      this.$store.commit('updatelistingsNoScope', [])
      if (this.isSingleTenantSelected) {
        await this.fetchListingsPerTenant()
      }
    },

    async fetchListingsPerTenant() {
      if (this.isSingleTenantSelected) {
        this.$store.dispatch('getlistingsNoScope', {
          tenant_id: this.selectedTenants[0],
        })
      }
    },

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedTenants = []
      } else {
        this.selectedTenants = this.tenants.map(tenant => tenant.id)
      }
    },
  },
}
</script>

<style scoped></style>
